import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/apiClient";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

interface CreateProductionBatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workOrderId: string;
}

export function CreateProductionBatchDialog({
  open,
  onOpenChange,
  workOrderId,
}: CreateProductionBatchDialogProps) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    batchNumber: "",
    quantity: 1,
    moldId: "",
    recipeId: "",
    machineId: "",
    notes: "",
  });

  // Fetch active molds
  const { data: molds } = useQuery({
    queryKey: ["molds", "active"],
    queryFn: async () => {
      const { data, error } = await api
        .from("molds")
        .select("*")
        .eq("is_active", true)
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  // Fetch active recipes
  const { data: recipes } = useQuery({
    queryKey: ["recipes", "active"],
    queryFn: async () => {
      const { data, error } = await api
        .from("production_recipes")
        .select("*")
        .eq("is_active", true)
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  // Fetch active machines
  const { data: machines } = useQuery({
    queryKey: ["machines", "active"],
    queryFn: async () => {
      const { data, error } = await api
        .from("machines")
        .select("*")
        .eq("is_active", true)
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (!formData.batchNumber || !formData.moldId || !formData.recipeId) {
      toast.error("Por favor complete los campos requeridos");
      return;
    }

    setLoading(true);
    try {
      const { error } = await api.from("production_batches").insert({
        batch_number: formData.batchNumber,
        work_order_id: workOrderId,
        recipe_id: formData.recipeId,
        mold_id: formData.moldId,
        machine_id: formData.machineId || null,
        quantity: formData.quantity,
        notes: formData.notes || null,
        status: "pending",
        created_by: user.id,
        start_date: new Date().toISOString(),
      });

      if (error) throw error;

      toast.success("Lote de producción creado exitosamente");
      queryClient.invalidateQueries({ queryKey: ["production-batches"] });
      onOpenChange(false);
      setFormData({
        batchNumber: "",
        quantity: 1,
        moldId: "",
        recipeId: "",
        machineId: "",
        notes: "",
      });
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Crear Lote de Producción</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="batchNumber">Número de Lote *</Label>
              <Input
                id="batchNumber"
                value={formData.batchNumber}
                onChange={(e) =>
                  setFormData({ ...formData, batchNumber: e.target.value })
                }
                required
                placeholder="LOTE-001"
              />
            </div>

            <div>
              <Label htmlFor="quantity">Cantidad *</Label>
              <Input
                id="quantity"
                type="number"
                min="1"
                value={formData.quantity}
                onChange={(e) =>
                  setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })
                }
                required
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="moldId">Molde *</Label>
              <Select
                value={formData.moldId}
                onValueChange={(value) => setFormData({ ...formData, moldId: value })}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar molde" />
                </SelectTrigger>
                <SelectContent>
                  {molds?.map((mold) => (
                    <SelectItem key={mold.id} value={mold.id}>
                      {mold.name} {mold.code && `(${mold.code})`}
                      {mold.max_uses && ` - ${mold.current_uses}/${mold.max_uses} usos`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="recipeId">Receta *</Label>
              <Select
                value={formData.recipeId}
                onValueChange={(value) => setFormData({ ...formData, recipeId: value })}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar receta" />
                </SelectTrigger>
                <SelectContent>
                  {recipes?.map((recipe) => (
                    <SelectItem key={recipe.id} value={recipe.id}>
                      {recipe.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="machineId">Máquina (Opcional)</Label>
            <Select
              value={formData.machineId}
              onValueChange={(value) => setFormData({ ...formData, machineId: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar máquina" />
              </SelectTrigger>
              <SelectContent>
                {machines?.map((machine) => (
                  <SelectItem key={machine.id} value={machine.id}>
                    {machine.name} {machine.model && `(${machine.model})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="notes">Notas</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              placeholder="Notas adicionales sobre el lote..."
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t.common.cancel}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? t.common.loading : "Crear Lote"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
